/**
 * The two pills that identify a store in a list, plus its label. Split out of
 * StoreList because StoreSwitcher renders `RoleTag` and `storeLabel` on their
 * own, for the single-store case where there is no list at all.
 *
 * They keep styling from `StoreList.module.scss`: the classes are elements of
 * the `store-list` block and renaming them would be a visual change, which this
 * refactor is not.
 */
import React from 'react'
import {formatWeekLabel} from '../lib/dateFormat'
import type {StoreItem, StoreWeekStatusItem} from './StoreContext'
import styles from './StoreList.module.scss'

/** "Nombre (CÓDIGO)" — the store's identity, code included when known. */
export const storeLabel = (store: StoreItem): string =>
  store.code !== undefined ? `${store.name} (${store.code})` : store.name

const roleLabel = (role: StoreItem['role']): string =>
  role === 'EDITOR' ? 'Editor' : 'Solo lectura'

/** Per-store role pill; nothing when the role is unknown. */
export const RoleTag = ({role}: {role: StoreItem['role']}): React.ReactElement | null => {
  if (role === undefined) return null
  return (
    <span
      className={[
        'itm-flx-s-0',
        'border-xs',
        'txt-bold-500',
        styles['store-list__role'],
        role === 'EDITOR' ? styles['store-list__role--editor'] : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {roleLabel(role)}
    </span>
  )
}

// Glyph + BEM modifier per status. Plain Unicode, not emoji —
// consistent with the search icon — colored via the modifier class.
//
// Two of these have ASCII lookalikes and are NOT those characters: NONE is
// U+2013 EN DASH (not a hyphen) and ZEROED is U+2715 MULTIPLICATION X (not the
// letter x). Both are deliberate: the ASCII ones render thinner and off-centre
// next to ✎ and ✓.
const STATUS_GLYPH: Record<StoreWeekStatusItem['status'], string> = {
  NONE: '–',
  PENDING: '✎',
  SAVED: '✓',
  ZEROED: '✕',
}

const STATUS_MODIFIER: Record<StoreWeekStatusItem['status'], string> = {
  NONE: 'none',
  PENDING: 'pending',
  SAVED: 'saved',
  ZEROED: 'zeroed',
}

/**
 * Per-store weekly-proposal indicator: pencil while pending
 * review, tick once saved, cross when saved-at-zero (opt-out) — plus the
 * (N) unit count. `undefined` (status still loading) renders nothing.
 *
 * The row NAMES its week, and the indicator says which: a store with a cutoff
 * of its own can be ordering for a different week than the store above it in
 * the very same list, so a glyph with no week is ambiguous exactly where it
 * matters. It goes in the title rather than on screen because the common case
 * is one week for everybody, where printing it on every row is noise.
 */
export const StoreStatusTag = ({status}: {status: StoreWeekStatusItem | undefined}): React.ReactElement | null => {
  if (status === undefined) return null
  return (
    <span
      title={formatWeekLabel(status.isoYear, status.isoWeek)}
      className={['flx-a-c', styles['store-list__status'], styles[`store-list__status--${STATUS_MODIFIER[status.status]}`]].join(' ')}
    >
      <span aria-hidden="true">{STATUS_GLYPH[status.status]}</span>
      {status.status !== 'NONE' && (
        <span className="txt-bold-500 txt-light-kale">({status.units})</span>
      )}
    </span>
  )
}
